/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  useResolvedTabRefreshHandler,
  useTabScreenRefreshContextOptional,
} from './TabScreenRefreshContext';
import { ticketColors } from '../../shared/theme/ticketColors';
import { showInfo } from '../../shared/ui/appToast';

/** Ancho del rail en web (solo refresh; sin nube offline). */
export const WEB_HEADER_TITLE_RIGHT_NARROW = 56;
export const WEB_HEADER_TITLE_RIGHT_WITH_REFRESH = 56;

export function WebTabDataRefreshButton() {
  const ctx = useTabScreenRefreshContextOptional();
  const refresh = useResolvedTabRefreshHandler();
  const [busy, setBusy] = useState(false);
  const spin = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!busy) {
      spin.stopAnimation();
      spin.setValue(0);
      return;
    }

    const useNativeDriver = Platform.OS !== 'web';
    const spinLoop = Animated.loop(
      Animated.timing(spin, {
        toValue: 1,
        duration: 820,
        easing: Easing.linear,
        useNativeDriver,
      })
    );

    spinLoop.start();

    return () => {
      spinLoop.stop();
    };
  }, [busy, spin]);

  const handlePress = useCallback(async () => {
    if (!refresh) {
      showInfo('Nada que actualizar', 'Esta pantalla no tiene datos para refrescar.');
      return;
    }
    setBusy(true);
    try {
      await Promise.resolve(refresh());
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  if (Platform.OS !== 'web' || !ctx) return null;

  return (
    <TouchableOpacity
      onPress={() => void handlePress()}
      disabled={busy}
      accessibilityRole="button"
      accessibilityLabel="Actualizar datos"
      accessibilityHint="Recarga los datos de esta pantalla. También podés pulsar F5 para recargar la página."
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      style={[styles.wrap, busy && styles.wrapBusy]}
      testID="header.web-refresh"
    >
      <View style={styles.iconWell}>
        <Animated.View
          style={{
            transform: [
              {
                rotate: spin.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '360deg'],
                }),
              },
            ],
          }}
        >
          <Ionicons name="refresh" size={20} color={busy ? ticketColors.gold : ticketColors.ink} />
        </Animated.View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
  },
  wrapBusy: {
    backgroundColor: ticketColors.brandSoft,
  },
  iconWell: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
